import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  DataTable,
  Badge,
  Button,
  TextField,
  Spinner,
  EmptyState,
  Toast,
  InlineStack,
  BlockStack,
  Text,
  Icon,
  ButtonGroup,
} from '@shopify/polaris';
import {
  SearchMinor,
  EditMinor,
  DeleteMinor,
} from '@shopify/polaris-icons';
import { usersService } from '../services/usersService';
import AddUserModal from '../components/modals/AddUserModal';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [addUserModalActive, setAddUserModalActive] = useState(false);
  const [toast, setToast] = useState(null);

  // Load users on component mount
  useEffect(() => {
    loadUsers();

    // Listen for custom events from App.js page actions
    const handleAddUser = () => setAddUserModalActive(true);
    const handleRefresh = () => loadUsers();

    window.addEventListener('usersAddUser', handleAddUser);
    window.addEventListener('usersRefresh', handleRefresh);

    return () => {
      window.removeEventListener('usersAddUser', handleAddUser);
      window.removeEventListener('usersRefresh', handleRefresh);
    };
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await usersService.getAllUsers();

      // Handle both array and object response
      const usersList = Array.isArray(usersData) ? usersData : usersData.users || [];

      setUsers(usersList);

      // Store users count in localStorage for App.js subtitle
      localStorage.setItem('usersData', JSON.stringify({
        totalUsers: usersList.length,
      }));
    } catch (error) {
      console.error('Failed to load users:', error);
      showToast('Failed to load users', true);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, isError = false) => {
    setToast({
      content: message,
      error: isError,
    });
  };

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleAddUserSuccess = () => {
    showToast('User created successfully!');
    loadUsers(); // Reload users
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await usersService.deleteUser(userId);
      showToast('User deleted successfully!');
      loadUsers(); // Reload users
    } catch (error) {
      console.error('Failed to delete user:', error);
      showToast('Failed to delete user', true);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    if (!searchValue) return true;

    const searchLower = searchValue.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.name?.toLowerCase().includes(searchLower) ||
      user.group?.toLowerCase().includes(searchLower)
    );
  });

  // Prepare rows for DataTable
  const rows = filteredUsers.map(user => {
    const groupBadge = user.group ? (
      <Badge tone={user.group === 'Super Admin' ? 'success' : 'info'}>
        {user.group}
      </Badge>
    ) : (
      <Badge tone="warning">No Group</Badge>
    );

    const statusBadge = user.deleted ? (
      <Badge tone="critical">Deleted</Badge>
    ) : (
      <Badge tone="success">Active</Badge>
    );

    const allowedCountries = Array.isArray(user.allowed_countries) ? user.allowed_countries : [];
    const countryAccessLabel = user.country_access_mode === 'all'
      ? 'All (EG, UAE)'
      : `${allowedCountries.join(', ') || 'EG'} (default: ${user.default_country || 'EG'})`;

    const actions = (
      <ButtonGroup>
        <Button
          size="slim"
          icon={EditMinor}
          onClick={() => {
            // TODO: Implement edit functionality
            showToast('Edit functionality coming soon');
          }}
        >
          Edit
        </Button>
        {!user.deleted && user.group !== 'Super Admin' && (
          <Button
            size="slim"
            tone="critical"
            icon={DeleteMinor}
            onClick={() => handleDeleteUser(user._id || user.id)}
          >
            Delete
          </Button>
        )}
      </ButtonGroup>
    );

    return [
      user.name || 'N/A',
      user.email || 'N/A',
      groupBadge,
      countryAccessLabel,
      statusBadge,
      new Date(user.created_at).toLocaleDateString() || 'N/A',
      actions,
    ];
  });

  const emptyStateMarkup = (
    <EmptyState
      heading="No users found"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>
        {searchValue
          ? 'Try adjusting your search terms'
          : 'Get started by adding your first user'}
      </p>
      {!searchValue && (
        <Button onClick={() => setAddUserModalActive(true)} variant="primary">
          Add User
        </Button>
      )}
    </EmptyState>
  );

  return (
    <>
      <Card>
        <BlockStack gap="400">
          {/* Search Bar */}
          <InlineStack align="space-between" blockAlign="center">
            <div style={{ width: '400px' }}>
              <TextField
                placeholder="Search by name, email, or group..."
                value={searchValue}
                onChange={handleSearchChange}
                prefix={<Icon source={SearchMinor} />}
                clearButton
                onClearButtonClick={() => setSearchValue('')}
                autoComplete="off"
              />
            </div>
            <Text variant="bodySm" tone="subdued">
              Showing {filteredUsers.length} of {users.length} users
            </Text>
          </InlineStack>

          {/* Loading State */}
          {loading && (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <Spinner size="large" />
              <Text variant="bodySm" tone="subdued" alignment="center">
                Loading users...
              </Text>
            </div>
          )}

          {/* Users Table */}
          {!loading && filteredUsers.length > 0 && (
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
              ]}
              headings={[
                'Name',
                'Email',
                'Group/Role',
                'Countries',
                'Status',
                'Created',
                'Actions',
              ]}
              rows={rows}
              hoverable
            />
          )}

          {/* Empty State */}
          {!loading && filteredUsers.length === 0 && emptyStateMarkup}
        </BlockStack>
      </Card>

      {/* Add User Modal */}
      <AddUserModal
        active={addUserModalActive}
        onClose={() => setAddUserModalActive(false)}
        onSuccess={handleAddUserSuccess}
      />

      {/* Toast Notifications */}
      {toast && (
        <Toast
          content={toast.content}
          onDismiss={() => setToast(null)}
          error={toast.error}
          duration={3000}
        />
      )}
    </>
  );
};

export default UsersPage;
